import { useLocalStorageState } from 'ahooks';
import { tFunnelInfo } from 'src/components/Chat/Chat.types';
import { tProvidersProps } from 'src/providers/LayoutProvider/Layout.types';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { markFunnelRecentlyEvicted } from 'src/providers/FunnelCacheProvider/recentlyEvictedFunnels';
import {
  mergeFunnelFromEvent,
  shallowEqualOnMergedFields,
} from 'src/providers/FunnelCacheProvider/FunnelCache.functions';
import { clearFunnelChecked } from 'src/providers/FunnelCacheProvider/staleFunnelsChecked';

// INT-945 origin / BUG-534 current behavior. The provider subscribes to
// `funnel_event.updated` socket events and reconciles the `funnelData`
// localStorage cache. Must use the ahooks `useLocalStorageState` setter (NOT
// raw `localStorage.setItem`) so SYNC_STORAGE_EVENT_NAME propagates the
// change to the live `Chat.hook` instance binding the same key.
//
// History:
//
//   INT-945 (origin, docs/plans/2026-05-25-001-...): the provider EVICTED
//   the matching funnel from cache on every `.updated`. Worked because the
//   next `Chat.hook.resetFunnelInfo` cache miss would re-fetch via
//   `getWorkspacesInfo`, which is the only channel carrying `users[]`. So a
//   newly-invited user appeared in the dropdown without a page reload.
//
//   BUG-534 (current, docs/plans/2026-05-26-002-...): in busy funnels the
//   `funnel_event.updated` fires every ~30-60s. EVICT emptied the cache for
//   each 30s `recentlyEvictedFunnels` TTL window, leaving SELLERs without
//   `can_view_handle` — the "Medio" row visibly flickered out (Birdie
//   `9YV4C6l43`, Armonia en el Hogar). This provider now MERGES scalars,
//   stages, and statuses from the payload, and marks `users_stale: true`.
//   The consumer-side lazy refetch in `Chat.hook.resetFunnelInfo` (PR #2425
//   extended in this plan's U3) refreshes `users[]` on next read, preserving
//   the INT-945 invariant.
//
// Residual risk: `funnelData` is a globally-keyed localStorage entry (not
// scoped per workspace), so the native `storage` event still propagates this
// tab's filtered cache to sibling tabs in other workspaces. The merge is
// workspace-scoped below, but cross-tab leakage requires namespacing the
// localStorage key (`funnelData:${workspaceId}`) across all readers/clearers
// — tracked as a follow-up.
export default function FunnelCacheInvalidationProvider(props: tProvidersProps) {
  const { children } = props;
  const { funnelUpdatedEvent } = useSocketsContext();
  const { currentWorkspace } = useWorkspaceContext();

  // BUG-534 second-pass: the merge now reads `prev` inside the functional
  // setter (not the outer-closure `funnelInfo`) so same-tick burst events
  // compose monotonic `cache_version` against the prior write. The
  // receiver-side binding is unused, but the hook still owns the
  // SYNC_STORAGE_EVENT_NAME dispatcher that propagates writes to
  // `Chat.hook`'s live consumer.
  const [, setFunnelInfo] = useLocalStorageState<tFunnelInfo[]>('funnelData', {
    defaultValue: [],
    listenStorageChange: true,
  });

  // Subscription timing — ahooks' `useSubscription` registers via
  // `useEffect([], [])`, which only runs after the first paint commit. Events
  // emitted between render and commit (e.g. an event replayed during a brief
  // socket reconnect, before this provider's effect has flushed) are silently
  // dropped. This is acceptable because the cache is best-effort: the next
  // `resetFunnelInfo` cache miss for the affected funnel triggers a fresh
  // fetch, and the dropdown self-heals on chat open. Fixing this at the
  // consumer layer would require subscribing to the raw socket via
  // `Socket.singleton` rather than through the EventEmitter — out of scope.
  //
  // Defensive optional-chain: `tFunnelEvent.response` is typed as the
  // all-required `Funnel`, but the socket runtime payload occasionally arrives
  // partial. Widening `response` to `Partial<Funnel>` cascades through every
  // other `tFunnelEvent` consumer (Pipe.hook, Sockets.hook); deferred to a
  // follow-up. The optional chain plus the `!updatedUuid` guard reflects the
  // real runtime contract.
  funnelUpdatedEvent.useSubscription((event) => {
    const updatedUuid = event?.response?.uuid;
    if (!updatedUuid) return;
    // Workspace-scope the merge: socket reconnect races can briefly deliver
    // events for other rooms, and a same-UUID match against the current
    // workspace's cache would write the wrong entry. If `currentWorkspace`
    // is not yet resolved, also skip — there is no cache to merge against
    // yet.
    if (event.workspace_id !== currentWorkspace?.id) return;

    // Race guards fire UNCONDITIONALLY before any cache-presence or
    // shallow-equal check, because each guard's failure mode is
    // independent of whether a cache entry currently exists or the merge
    // is observationally vacuous:
    //
    //   markFunnelRecentlyEvicted — suppresses any in-flight
    //   `Chat.hook.resetFunnelInfo` cache-miss fetch whose pre-merge
    //   response would otherwise land in the cache. The empty-cache case
    //   matters: a consumer opening a chat dispatches the cache-miss
    //   fetch before this event arrives, and a same-tick `.updated` for
    //   the same funnel must still suppress the consumer's late cache
    //   write or INT-945 regresses for not-yet-opened funnels.
    //
    //   clearFunnelChecked — re-opens the lazy-refetch throttle so the
    //   next `resetFunnelInfo` call forces a fresh `getWorkspacesInfo`.
    //   Must fire even on vacuous merges (empty arrays / partial
    //   payloads): the throttle is keyed by uuid alone, and a stuck
    //   throttle (set by a prior failed BG fetch, or by a successful
    //   fetch followed by no clearing event) keeps `users[]` stale for
    //   the session.
    markFunnelRecentlyEvicted(updatedUuid);
    clearFunnelChecked(updatedUuid);

    // Merge against the FRESHEST cache state — `prev` inside the
    // functional updater — not the outer-closure `funnelInfo`. Two
    // `funnel_event.updated` events fired in the same React tick must
    // both observe a monotonic `cache_version`; reading `existing` from
    // a stale closure makes the second event compute `existing.cache_version + 1`
    // against the pre-first-event snapshot and lose the first event's
    // scalar writes.
    //
    // Suppress vacuous writes when nothing observable changed. Note: any
    // payload carrying a non-empty `stages` or `statuses` produces fresh
    // array references via `.map()` in the merger, so this short-circuit
    // mostly fires on partial payloads (empty / omitted arrays). Per-event
    // write cost in busy funnels is accepted as a known cost — the structural
    // fix is BE emission dampening (tracked as a follow-up). See plan
    // 2026-05-26-002 Key Technical Decisions.
    setFunnelInfo((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      if (safePrev.length === 0) return safePrev;
      const idx = safePrev.findIndex((funnel) => funnel.uuid === updatedUuid);
      if (idx === -1) return safePrev;
      const current = safePrev[idx];
      const merged = mergeFunnelFromEvent(event.response, current);
      if (shallowEqualOnMergedFields(merged, current)) return safePrev;
      const next = [...safePrev];
      next[idx] = merged;
      return next;
    });
  });

  return <>{children}</>;
}
